import axios from 'axios';
import { API_BASE_URL } from '../config';

export interface Animal {
  id?: number;
  proprietaire: number;
  proprietaire_nom?: string;
  proprietaire_details?: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
    telephone: string;
  };
  nom: string;
  date_naissance?: string;
  espece: string;
  race?: string;
  poids?: number;
  sexe: 'M' | 'F';
  sterilise: boolean;
  numero_puce?: string;
  numero_tatouage?: string;
  photo_url?: string;
  date_creation?: string;
  date_modification?: string;
}

export interface CreateAnimalData {
  nom: string;
  date_naissance?: string;
  espece: string;
  race?: string;
  poids?: number;
  sexe: 'M' | 'F';
  sterilise: boolean;
  numero_puce?: string;
  numero_tatouage?: string;
  photo_url?: string;
}

export interface AnimalListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Animal[];
}

// Configuration axios avec intercepteurs pour JWT
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Intercepteur pour ajouter le token JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expiré, tenter de le rafraîchir
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          const newToken = response.data.access;
          localStorage.setItem('access_token', newToken);
          
          // Retry la requête originale avec le nouveau token
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        } catch (refreshError) {
          // Échec du refresh, rediriger vers login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const animalService = {
  // Récupérer tous les animaux du propriétaire connecté
  async getMyAnimals(params?: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<AnimalListResponse> {
    const response = await api.get('/animaux/my_animals/', { params });
    return response.data;
  },

  // Récupérer tous les animaux (admin/vétérinaire)
  async getAll(params?: {
    page?: number;
    page_size?: number;
    proprietaire?: number;
    search?: string;
  }): Promise<AnimalListResponse> {
    const response = await api.get('/animaux/', { params });
    return response.data;
  },

  // Récupérer un animal par ID
  async getById(id: number): Promise<Animal> {
    const response = await api.get(`/animaux/${id}/`);
    return response.data;
  },

  // Créer un nouvel animal
  async create(animalData: CreateAnimalData): Promise<Animal> {
    const response = await api.post('/animaux/', animalData);
    return response.data;
  },

  // Mettre à jour un animal
  async update(id: number, animalData: Partial<CreateAnimalData>): Promise<Animal> {
    const response = await api.put(`/animaux/${id}/`, animalData);
    return response.data;
  },

  // Mettre à jour partiellement un animal
  async partialUpdate(id: number, animalData: Partial<CreateAnimalData>): Promise<Animal> {
    const response = await api.patch(`/animaux/${id}/`, animalData);
    return response.data;
  },

  // Supprimer un animal
  async delete(id: number): Promise<void> {
    await api.delete(`/animaux/${id}/`);
  },

  // Rechercher des animaux
  async search(query: string, filters?: {
    proprietaire?: number;
    espece?: string;
  }): Promise<AnimalListResponse> {
    const params = {
      search: query,
      ...filters,
    };
    const response = await api.get('/animaux/', { params });
    return response.data;
  },
}; 